import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import * as React from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { canonicalApi, type ValidateResponse } from "@/lib/api-canonical";

const TEMPLATE_NEW_TYPE = `canonical_type: my.new.entity
version: 1
description: Replace with a meaningful description.
fields:
  uid:
    type: string
    required: true
  created_at:
    type: datetime
    required: true
`;

type Mode =
  | { kind: "new-type" }
  | { kind: "new-version"; baseYaml: string; nextVersion: number; canonical_type: string };

export interface NewCanonicalDialogProps {
  open: boolean;
  mode: Mode;
  onOpenChange: (open: boolean) => void;
  onCreated: (type: string, version: number) => void;
}

function initialYamlFor(mode: Mode): string {
  if (mode.kind === "new-version") {
    // Bump the version line in the existing YAML.
    return mode.baseYaml.replace(
      /^version:\s*\d+/m,
      `version: ${mode.nextVersion}`,
    );
  }
  return TEMPLATE_NEW_TYPE;
}

export function NewCanonicalDialog({
  open,
  mode,
  onOpenChange,
  onCreated,
}: NewCanonicalDialogProps) {
  const qc = useQueryClient();
  const [yaml, setYaml] = React.useState(() => initialYamlFor(mode));
  const [validation, setValidation] = React.useState<ValidateResponse | null>(null);
  const [tags, setTags] = React.useState<string[]>([]);
  const fileInput = React.useRef<HTMLInputElement>(null);

  // Reset content whenever the dialog opens (mode may have changed).
  React.useEffect(() => {
    if (open) {
      setYaml(initialYamlFor(mode));
      setValidation(null);
      setTags([]);
    }
  }, [open, mode]);

  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const r = await canonicalApi.validate(yaml);
        setValidation(r);
      } catch {
        /* ignore */
      }
    }, 350);
    return () => clearTimeout(t);
  }, [yaml, open]);

  const createMutation = useMutation({
    mutationFn: () => canonicalApi.create(yaml, tags),
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ["canonical"] });
      onCreated(detail.canonical_type, detail.version);
      onOpenChange(false);
    },
  });

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then(setYaml);
    e.target.value = "";
  }

  const title =
    mode.kind === "new-version"
      ? `New version — ${mode.canonical_type} v${mode.nextVersion}`
      : "New canonical type (draft)";

  const subtitle =
    mode.kind === "new-version"
      ? "Pre-populated with the active YAML. Modify, then save as draft."
      : "Define a new canonical type. Will be created as draft.";

  const versionMismatch =
    mode.kind === "new-version" &&
    validation?.valid &&
    (validation.canonical_type !== mode.canonical_type || validation.version !== mode.nextVersion);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div>
            <DialogTitle className="text-sm font-semibold">{title}</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          </div>
        </DialogHeader>
        <DialogBody className="p-0">
          <div className="flex items-center gap-2 border-b border-border px-4 h-10 bg-surface">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInput.current?.click()}
              type="button"
            >
              <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />
              Upload .yaml
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".yaml,.yml,text/yaml"
              className="hidden"
              onChange={handleUpload}
            />
            <div className="ml-auto">
              {validation?.valid ? (
                <span className="text-xs text-success">Valid YAML</span>
              ) : validation ? (
                <span className="text-xs text-danger">{validation.errors.length} error(s)</span>
              ) : (
                <span className="text-xs text-muted-foreground">Type or upload…</span>
              )}
            </div>
          </div>

          <div className="h-72">
            <CodeEditor value={yaml} onChange={setYaml} language="yaml" />
          </div>

          <div className="space-y-1.5 border-t border-border px-4 py-3">
            <Label htmlFor="new-canonical-tags">Tags (optional)</Label>
            <TagsField
              id="new-canonical-tags"
              value={tags}
              onChange={setTags}
              scope="canonicals"
              placeholder="e.g. domain:warehouse, shared"
            />
          </div>

          {validation && !validation.valid && (
            <div className="border-t border-border px-4 py-2">
              <Alert tone="danger" title="Validation errors">
                <ul className="list-disc pl-4">
                  {validation.errors.map((e, i) => (
                    <li key={i} className="font-mono text-xs">{e}</li>
                  ))}
                </ul>
              </Alert>
            </div>
          )}
          {versionMismatch && (
            <div className="border-t border-border px-4 py-2">
              <Alert tone="warning" title="Type or version mismatch">
                Expected <span className="font-mono">{mode.canonical_type} v{mode.nextVersion}</span>,
                got <span className="font-mono">{validation?.canonical_type} v{validation?.version}</span>.
              </Alert>
            </div>
          )}
          {createMutation.error && (
            <div className="border-t border-border px-4 py-2">
              <Alert tone="danger" title="Create failed">
                {(createMutation.error as Error).message}
              </Alert>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!validation?.valid || !!versionMismatch || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
